/**
 * DP 链数据访问 —— 经 meshlink 服务端 `/chain-api/*` 代理直通链 8099。
 * 链浏览器 / 独立钱包共用此封装，前端同源调用，无需 Basic Auth 与 App Key。
 */

export async function chainGet<T>(path: string): Promise<T> {
  const r = await fetch('/chain-api/' + path, { headers: { Accept: 'application/json' } });
  const j = (await r.json().catch(() => ({}))) as { error?: string };
  if (!r.ok) throw new Error(j.error ?? 'HTTP ' + r.status);
  return j as unknown as T;
}

export async function chainPost<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch('/chain-api/' + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const j = (await r.json().catch(() => ({}))) as { error?: string };
  if (!r.ok) throw new Error(j.error ?? 'HTTP ' + r.status);
  return j as unknown as T;
}

/** 链状态（/api/v1/status 的 data 段）。 */
export interface ChainStatus {
  height: number;
  genesis_timestamp: number;
  latest_block_timestamp: number;
  producing: boolean;
  interval: number;
  uptime: number;
  mempool_size: number;
  block_rate_1m: number;
  cap: number;
  total_supply: number;
  released: number;
  pools?: { flow?: number; stable?: number; treasury?: number };
  mint_stats?: { mint_total: number; mint_count: number; faucet_total: number; ops_mint_total: number };
  rel_pct?: number;
}

/** 区块（/api/blocks 数组元素）。 */
export interface ChainBlock {
  index: number;
  timestamp: number;
  transactions: unknown[];
  proposer: string;
  prev_hash: string;
  merkle_root: string;
  nonce: number;
  hash: string;
}

/** 账户（/api/accounts 数组元素）。 */
export interface ChainAccount {
  address: string;
  balance: number;
  [k: string]: unknown;
}

export const fmtDpb = (microOrDpb: number): string => {
  // 账户/池余额为 DPB 浮点；金额与交易为 micro（1e6）。此处按数值展示（>1e5 视为 micro 转 DPB）
  const v = Math.abs(microOrDpb) > 1e5 ? microOrDpb / 1e6 : microOrDpb;
  return v.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
};

export const fmtTime = (ts: number): string =>
  new Date(ts * 1000).toLocaleString('zh-CN', { hour12: false });

export const shortAddr = (a: string): string =>
  a.length > 14 ? a.slice(0, 8) + '…' + a.slice(-6) : a;
